<script>
export default { name: 'UiFileUpload' };
</script>

<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { Upload, X, Loader2, FileText } from 'lucide-vue-next';

let uidSeed = 0;

// Dropzone con drag & drop para subir archivos. El componente NO sube el archivo:
// emite `files-selected` con los File válidos y el padre sube con useUpload()
// (POST /upload) y devuelve la URL por v-model, igual que handleImageUpload del
// Form de customers. Mientras el padre sube, muestra el overlay de carga (loading).
//
// Uso típico:
//   <UiFileUpload v-model="form.document_front_url" label="Documento Frente"
//     accept="image/*" :max-size="2 * 1024 * 1024" :loading="isUploadingFront"
//     @files-selected="(files) => handleImageUpload(files[0], 'document_front_url')" />
const props = defineProps({
  // URL(es) ya subida(s) a mostrar en la preview (backend). En multiple es un array.
  modelValue: { type: [String, Array, File, Object], default: null },
  label: { type: String, default: '' },
  required: { type: Boolean, default: false },
  dropzoneText: {
    type: String,
    default: 'Arrastra el archivo aquí o haz clic para seleccionar',
  },
  // accept nativo del input, ej. "image/*", ".pdf", "application/pdf".
  accept: { type: String, default: '' },
  multiple: { type: Boolean, default: false },
  // Tamaño máximo por archivo en bytes. 0 = sin límite.
  maxSize: { type: Number, default: 0 },
  disabled: { type: Boolean, default: false },
  // Muestra el overlay de carga mientras el padre sube el archivo.
  loading: { type: Boolean, default: false },
  loadingText: { type: String, default: 'Subiendo archivo...' },
  error: { type: String, default: '' },
  hint: { type: String, default: '' },
  // Componente lucide de la dropzone, ej. :icon="ImagePlus".
  icon: { type: [Object, Function, String], default: null },
  // Permite quitar un archivo ya subido desde la preview.
  allowRemove: { type: Boolean, default: true },
  id: { type: String, default: null },
});

const emit = defineEmits([
  'update:modelValue',
  'change',
  'files-selected',
  'remove',
  'error',
  'focus',
  'blur',
]);

const fieldId = props.id || `ui-file-${++uidSeed}`;
const inputRef = ref(null);
const isDragging = ref(false);
const localError = ref('');
// Previews locales mientras el padre sube el archivo (antes de recibir la URL).
const pendingFiles = ref([]);
const objectUrls = new Map();

const displayedIcon = computed(() => props.icon || Upload);
const errorMessage = computed(() => props.error || localError.value);

const isFile = (v) => v instanceof File;
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|svg|bmp|avif|heic|tiff?)([?#].*)?$/i;

function looksLikeImage(url) {
  return typeof url === 'string' && IMAGE_EXT.test(url.split('?')[0]);
}

function fileNameFromUrl(url) {
  try {
    const u = new URL(url);
    const seg = u.pathname.split('/').filter(Boolean).pop();
    return decodeURIComponent(seg || url);
  } catch {
    const seg = String(url).split(/[\\/]/).pop();
    return seg || String(url);
  }
}

function formatBytes(bytes) {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value = value / 1024;
    i += 1;
  }
  const decimals = i === 0 || value >= 10 ? 0 : 1;
  return `${value.toFixed(decimals)} ${units[i]}`;
}

function objectUrlFor(file) {
  if (!isFile(file)) return '';
  const key = `${file.name}:${file.size}:${file.lastModified}`;
  if (!objectUrls.has(key)) objectUrls.set(key, URL.createObjectURL(file));
  return objectUrls.get(key);
}

const acceptHint = computed(() => {
  const parts = [];
  if (props.accept) parts.push(props.accept.replace(/image\/\*/g, 'Imágenes'));
  if (props.maxSize) parts.push(`máx. ${formatBytes(props.maxSize)}`);
  return parts.join(' · ');
});

const singleState = computed(() => {
  if (props.multiple) return null;
  const pending = pendingFiles.value[0];
  if (pending) {
    return {
      url: objectUrlFor(pending),
      label: pending.name,
      isImage: pending.type.startsWith('image/'),
      pending: true,
    };
  }
  const mv = props.modelValue;
  if (!mv) return null;
  if (isFile(mv)) {
    return {
      url: objectUrlFor(mv),
      label: mv.name,
      isImage: mv.type.startsWith('image/'),
      pending: false,
    };
  }
  const url = String(mv);
  return { url, label: fileNameFromUrl(url), isImage: looksLikeImage(url), pending: false };
});

const multiItems = computed(() => {
  if (!props.multiple) return [];
  const out = [];
  const modelList = Array.isArray(props.modelValue) ? props.modelValue : [];
  modelList.forEach((v, i) => {
    if (isFile(v)) {
      out.push({
        key: `m-${i}`,
        url: objectUrlFor(v),
        label: v.name,
        isImage: v.type.startsWith('image/'),
        source: 'model',
        sourceIndex: i,
        pending: false,
      });
    } else {
      const url = String(v || '');
      out.push({
        key: `m-${i}`,
        url,
        label: fileNameFromUrl(url),
        isImage: looksLikeImage(url),
        source: 'model',
        sourceIndex: i,
        pending: false,
      });
    }
  });
  pendingFiles.value.forEach((f, i) => {
    out.push({
      key: `p-${i}`,
      url: objectUrlFor(f),
      label: f.name,
      isImage: f.type.startsWith('image/'),
      source: 'pending',
      sourceIndex: i,
      pending: true,
    });
  });
  return out;
});

const showEmpty = computed(() =>
  props.multiple ? multiItems.value.length === 0 : !singleState.value
);

function acceptFile(file) {
  if (!props.accept) return true;
  const patterns = props.accept.split(',').map((p) => p.trim().toLowerCase()).filter(Boolean);
  const ext = `.${(file.name.split('.').pop() || '').toLowerCase()}`;
  return patterns.some((pattern) => {
    if (pattern === '*/*') return true;
    if (pattern.startsWith('.')) return ext === pattern;
    const [type, sub] = pattern.split('/');
    if (sub === '*') return String(file.type || '').toLowerCase().startsWith(`${type}/`);
    return String(file.type || '').toLowerCase() === pattern;
  });
}

function handleFiles(fileList) {
  if (props.disabled || props.loading) return;
  const files = Array.from(fileList || []);
  if (!files.length) return;

  localError.value = '';
  const rejected = [];
  const valid = [];

  files.forEach((file) => {
    if (!acceptFile(file)) rejected.push({ file, reason: 'type' });
    else if (props.maxSize > 0 && file.size > props.maxSize) rejected.push({ file, reason: 'size' });
    else valid.push(file);
  });

  if (rejected.length) {
    const messages = rejected.map(({ file, reason }) =>
      reason === 'size'
        ? `"${file.name}" excede el tamaño máximo (${formatBytes(props.maxSize)})`
        : `"${file.name}" no es un tipo de archivo permitido`
    );
    localError.value = messages.join(' · ');
    emit('error', { files: rejected, message: localError.value });
  }

  if (!valid.length) return;

  if (props.multiple) {
    pendingFiles.value = [...pendingFiles.value, ...valid];
  } else {
    pendingFiles.value = [valid[0]];
  }

  emit('files-selected', valid);
  emit('change', { file: valid[0], files: valid });
}

function onInputChange(event) {
  handleFiles(event.target.files);
  event.target.value = '';
}

function onClickRoot(event) {
  if (props.disabled || props.loading) return;
  if (event.target === inputRef.value) return;
  inputRef.value?.click();
}

function onDragOver(event) {
  if (props.disabled || props.loading) return;
  event.preventDefault();
  isDragging.value = true;
}

function onDragLeave(event) {
  event.preventDefault();
  isDragging.value = false;
}

function onDrop(event) {
  event.preventDefault();
  isDragging.value = false;
  if (props.disabled || props.loading) return;
  handleFiles(event.dataTransfer ? event.dataTransfer.files : null);
}

// Cuando el padre devuelve la URL (upload terminado) descartamos el preview local.
watch(
  () => props.modelValue,
  (value) => {
    if (props.multiple) {
      if (Array.isArray(value) && value.length) pendingFiles.value = [];
    } else if (value && !isFile(value)) {
      pendingFiles.value = [];
    }
  }
);

function removeSingle(item) {
  if (props.disabled || props.loading) return;
  pendingFiles.value = [];
  emit('remove', { item: item ? item.url : null });
  emit('update:modelValue', null);
}

function removeItem(item) {
  if (props.disabled || props.loading) return;
  emit('remove', { item: item.url, source: item.source });
  if (item.source === 'pending') {
    pendingFiles.value = pendingFiles.value.filter((_, i) => i !== item.sourceIndex);
  } else {
    const next = (Array.isArray(props.modelValue) ? [...props.modelValue] : []).filter(
      (_, i) => i !== item.sourceIndex
    );
    emit('update:modelValue', next);
  }
}

onBeforeUnmount(() => {
  objectUrls.forEach((url) => URL.revokeObjectURL(url));
  objectUrls.clear();
});
</script>

<template>
  <div>
    <label
      v-if="label"
      :for="fieldId"
      class="block text-xs font-semibold text-slate-500 uppercase mb-1"
    >
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>

    <div
      :class="[
        'relative border-2 border-dashed rounded-xl transition-all overflow-hidden focus-within:ring-4 focus-within:ring-brand-500/10',
        isDragging
          ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/10'
          : errorMessage
            ? 'border-red-400 focus-within:border-red-500 focus-within:ring-red-500/10'
            : 'border-slate-300 dark:border-slate-700 focus-within:border-brand-500 focus-within:ring-brand-500/10',
        disabled || loading
          ? 'opacity-60 cursor-not-allowed'
          : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60',
      ]"
      @click="onClickRoot"
      @dragover.prevent="onDragOver"
      @dragenter.prevent="onDragOver"
      @dragleave="onDragLeave"
      @drop.prevent="onDrop"
    >
      <!-- Estado vacío -->
      <div v-if="showEmpty" class="flex flex-col items-center justify-center px-4 py-10 text-center">
        <component :is="displayedIcon" class="w-8 h-8 text-slate-400 mb-2" aria-hidden="true" />
        <span class="text-sm font-semibold text-slate-600 dark:text-slate-300">{{ dropzoneText }}</span>
        <span v-if="acceptHint" class="text-xs text-slate-400 mt-1">{{ acceptHint }}</span>
      </div>

      <!-- Preview de un solo archivo -->
      <div v-else-if="singleState" class="relative group">
        <img
          v-if="singleState.isImage"
          :src="singleState.url"
          :alt="singleState.label"
          class="w-full h-40 object-contain bg-slate-100 dark:bg-slate-900"
        />
        <div
          v-else
          class="w-full h-40 flex flex-col items-center justify-center gap-2 bg-slate-50 dark:bg-slate-800 px-4"
        >
          <FileText class="w-10 h-10 text-slate-400" />
          <span class="text-xs font-semibold text-slate-600 dark:text-slate-300 text-center break-all">
            {{ singleState.label }}
          </span>
        </div>

        <div
          class="absolute inset-0 hidden group-hover:flex items-center justify-center gap-1.5 bg-black/40 text-white transition-opacity"
        >
          <Upload class="w-4 h-4" />
          <span class="text-xs font-bold uppercase">Reemplazar</span>
        </div>

        <button
          v-if="allowRemove && !loading"
          type="button"
          :aria-label="`Quitar ${singleState.label}`"
          class="absolute top-2 right-2 p-1.5 rounded-lg bg-white/90 dark:bg-slate-900/90 text-slate-500 hover:text-red-500 shadow-sm transition-colors"
          @click.stop="removeSingle(singleState)"
        >
          <X class="w-4 h-4" />
        </button>
      </div>

      <!-- Preview de múltiples archivos -->
      <div v-else class="p-3 space-y-2">
        <div
          v-for="item in multiItems"
          :key="item.key"
          class="flex items-center gap-3 border border-slate-200 dark:border-slate-700 rounded-lg p-2 bg-white dark:bg-slate-900"
        >
          <img
            v-if="item.isImage"
            :src="item.url"
            :alt="item.label"
            class="w-10 h-10 rounded object-cover flex-shrink-0"
          />
          <FileText v-else class="w-6 h-6 text-slate-400 flex-shrink-0" />
          <span class="flex-1 text-xs font-semibold text-slate-600 dark:text-slate-300 truncate">
            {{ item.label }}
          </span>
          <Loader2 v-if="item.pending" class="w-4 h-4 animate-spin text-brand-500 flex-shrink-0" />
          <button
            v-if="allowRemove && !loading"
            type="button"
            :aria-label="`Quitar ${item.label}`"
            class="p-1 rounded-lg text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
            @click.stop="removeItem(item)"
          >
            <X class="w-4 h-4" />
          </button>
        </div>
        <div
          class="flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-slate-600 transition-colors pt-0.5"
        >
          <Upload class="w-4 h-4" />
          <span>Agregar más archivos</span>
        </div>
      </div>

      <!-- Overlay drag & drop -->
      <div
        v-if="isDragging"
        class="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-brand-600/90 text-white pointer-events-none"
      >
        <Upload class="w-8 h-8 animate-bounce" aria-hidden="true" />
        <span class="text-sm font-bold uppercase tracking-wide">Suelta el archivo aquí</span>
      </div>

      <!-- Overlay de carga -->
      <div
        v-if="loading"
        class="absolute inset-0 z-30 flex flex-col items-center justify-center gap-2 bg-black/50 text-white pointer-events-none"
      >
        <Loader2 class="w-8 h-8 animate-spin" aria-hidden="true" />
        <span class="text-xs font-bold uppercase tracking-wide">{{ loadingText }}</span>
      </div>

      <input
        :id="fieldId"
        ref="inputRef"
        type="file"
        class="sr-only"
        :accept="accept"
        :multiple="multiple"
        :disabled="disabled"
        @click.stop
        @change="onInputChange"
        @focus="$emit('focus', $event)"
        @blur="$emit('blur', $event)"
      />
    </div>

    <p v-if="errorMessage" class="text-xs text-red-500 mt-1">{{ errorMessage }}</p>
    <p v-else-if="hint" class="text-xs text-slate-400 mt-1">{{ hint }}</p>
  </div>
</template>